/**
 * Quick Start Service
 *
 * API service for the Quick Start onboarding flow.
 * Handles checking status, updating preferences, and importing BGG collections.
 */

import { API_BASE } from "../config/api";
import { httpRequest } from "../utils/httpClient";
import { authService } from "./auth";

/**
 * Fetch Quick Start status for current user
 * @returns {Promise<{
 *   needs_quick_start: boolean,
 *   reasons: string[],
 *   bgg_id: string|null,
 *   collection_count: number,
 *   session_count: number,
 *   screens_needed: number[],
 *   onboarding_preferences: object|null
 * }>}
 */
export async function getQuickStartStatus() {
  const res = await httpRequest(`${API_BASE}/profile/quick-start-status`, {
    method: "GET",
    headers: authService.getAuthHeaders(),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to get quick start status" }));
    throw new Error(error.detail || "Failed to get quick start status");
  }

  return res.json();
}

/**
 * Update onboarding preferences
 * @param {{
 *   bgg_id_opt_out?: boolean,
 *   quick_start_completed?: boolean
 * }} preferences
 * @returns {Promise<{success: boolean, onboarding_preferences: object}>}
 */
export async function updateOnboardingPreferences(preferences) {
  const res = await httpRequest(`${API_BASE}/profile/onboarding-preferences`, {
    method: "PUT",
    headers: {
      ...authService.getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(preferences),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to update onboarding preferences" }));
    throw new Error(error.detail || "Failed to update onboarding preferences");
  }

  return res.json();
}

/**
 * Update BGG ID for current user
 * @param {string} bggId
 * @returns {Promise<{success: boolean, bgg_id: string}>}
 */
export async function updateBggId(bggId) {
  const res = await httpRequest(`${API_BASE}/profile/bgg-id`, {
    method: "PUT",
    headers: {
      ...authService.getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ bgg_id: bggId }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to update BGG ID" }));
    throw new Error(error.detail || "Failed to update BGG ID");
  }

  return res.json();
}

/**
 * Import BGG collection for current user
 * @returns {Promise<{
 *   success: boolean,
 *   imported_count: number,
 *   updated_count: number,
 *   total_games: number,
 *   errors: string[]
 * }>}
 */
export async function importBggCollection() {
  const res = await httpRequest(`${API_BASE}/profile/collection/import-bgg`, {
    method: "POST",
    headers: authService.getAuthHeaders(),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to import BGG collection" }));
    throw new Error(error.detail || "Failed to import BGG collection");
  }

  return res.json();
}

/**
 * Add a game to user's collection
 * @param {{
 *   game_id: number,
 *   preference?: string,
 *   ownership_status?: string,
 *   has_played?: boolean,
 *   additional_tags?: string[]
 * }} gameData
 * @returns {Promise<{success: boolean, game_id: number}>}
 */
export async function addToCollection(gameData) {
  const res = await httpRequest(`${API_BASE}/profile/collection`, {
    method: "POST",
    headers: {
      ...authService.getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(gameData),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to add game to collection" }));
    throw new Error(error.detail || "Failed to add game to collection");
  }

  return res.json();
}

/**
 * Get collection total count
 * @returns {Promise<{total_count: number}>}
 */
export async function getCollectionTotalCount() {
  const res = await httpRequest(`${API_BASE}/profile/collection/total-count`, {
    method: "GET",
    headers: authService.getAuthHeaders(),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to get collection count" }));
    throw new Error(error.detail || "Failed to get collection count");
  }

  return res.json();
}

/**
 * Get user's collection games
 * @returns {Promise<Array<{game_id: number, name: string, thumbnail?: string}>>}
 */
export async function getCollectionGames() {
  const res = await httpRequest(`${API_BASE}/profile/collection`, {
    method: "GET",
    headers: authService.getAuthHeaders(),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to get collection games" }));
    throw new Error(error.detail || "Failed to get collection games");
  }

  return res.json();
}

/**
 * Create a simplify/play session
 * @param {{
 *   chosen_game_id: number,
 *   chosen_at: string,
 *   participants?: Array<{user_id: number, status: string}>,
 *   ratings?: object,
 *   winners?: number[]
 * }} sessionData
 * @returns {Promise<object>}
 */
export async function createSimplifySession(sessionData) {
  const res = await httpRequest(`${API_BASE}/simplify/session`, {
    method: "POST",
    headers: {
      ...authService.getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(sessionData),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to create session" }));
    throw new Error(error.detail || "Failed to create session");
  }

  return res.json();
}

/**
 * Batch create sessions from collection games
 * Creates first N sessions synchronously, rest in background
 * @param {{
 *   game_ids: number[],
 *   batch_size?: number
 * }} batchData
 * @returns {Promise<{
 *   success: boolean,
 *   task_id?: string,
 *   created_count: number,
 *   total_count: number,
 *   message: string
 * }>}
 */
export async function batchCreateSessions(batchData) {
  const res = await httpRequest(`${API_BASE}/simplify/sessions/batch-create`, {
    method: "POST",
    headers: {
      ...authService.getAuthHeaders(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(batchData),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to create sessions batch" }));
    throw new Error(error.detail || "Failed to create sessions batch");
  }

  return res.json();
}

/**
 * Get status of batch session creation task
 * @param {string} taskId
 * @returns {Promise<object>}
 */
export async function getBatchSessionStatus(taskId) {
  const res = await httpRequest(`${API_BASE}/simplify/sessions/batch-status/${taskId}`, {
    method: "GET",
    headers: authService.getAuthHeaders(),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to get batch status" }));
    throw new Error(error.detail || "Failed to get batch status");
  }

  return res.json();
}

/**
 * Create sessions from collection dates
 * @returns {Promise<{sessions_created: number}>}
 */
export async function createSessionsFromCollectionDates() {
  const res = await httpRequest(`${API_BASE}/simplify/sessions/from-collection`, {
    method: "POST",
    headers: authService.getAuthHeaders(),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: "Failed to create sessions from collection dates" }));
    throw new Error(error.detail || "Failed to create sessions from collection dates");
  }

  return res.json();
}

/**
 * Mark Quick Start as completed
 * @returns {Promise<{success: boolean}>}
 */
export async function markQuickStartCompleted() {
  return updateOnboardingPreferences({ quick_start_completed: true });
}

/**
 * Mark BGG ID as opted out
 * @returns {Promise<{success: boolean}>}
 */
export async function markBggIdOptOut() {
  return updateOnboardingPreferences({ bgg_id_opt_out: true });
}

/**
 * Clear Quick Start completed status to re-trigger onboarding
 * @returns {Promise<{success: boolean}>}
 */
export async function clearQuickStartCompleted() {
  return updateOnboardingPreferences({ quick_start_completed: false });
}

const quickStartService = {
  getQuickStartStatus,
  updateOnboardingPreferences,
  updateBggId,
  importBggCollection,
  addToCollection,
  getCollectionTotalCount,
  getCollectionGames,
  createSimplifySession,
  batchCreateSessions,
  getBatchSessionStatus,
  createSessionsFromCollectionDates,
  markQuickStartCompleted,
  markBggIdOptOut,
  clearQuickStartCompleted,
};

export default quickStartService;
